import React, { useContext, useEffect, useState } from 'react';
import styles from './PermissionModal.module.css';
import ModalShell from 'components/shared/modal/ModalShell';
import useTheme from 'config/useTheme';
import ModalContext from 'config/context/modalContext';
import { useCreatePermission } from 'api/permissions/usePermissions';
import { useGenerateKeypair } from 'api/authentication/usePrivateKey';
import Loader from 'components/shared/loader/Loader';
import SuccessfullActionModal from './SuccessfullActionModal';
import { testPermissionName } from 'utils/validations';
import downloadFile from 'lib/DownloadFile';
import { ExportIcon } from 'assets/icons';

const CreatePermissionModal = ({ permissionParentName, finalStepDuration }) => {
  const { dark } = useTheme();
  const { handleModal } = useContext(ModalContext);
  const [isValid, setIsValid] = useState(false);
  const [data, setData] = useState({
    permission_name: '',
    permission_parent_name: permissionParentName,
    permission_parent_private_key: '',
    permission_public_key: ''
  });

  const {
    response,
    loading,
    error,
    setNewPermission,
    resetServerStatus
  } = useCreatePermission({
    permissionName: data?.permission_name,
    permissionParentName: data?.permission_parent_name,
    permissionParentPrivateKey: data?.permission_parent_private_key,
    permissionPublicKey: data?.permission_public_key,
  });

  const {
    response: keypairResponse,
    loading: keypairLoading,
    error: keypairError,
    getKeypair,
    resetServerStatus: keypairResetServerStatus
  } = useGenerateKeypair();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'permission_name') {
      if (value === '' || testPermissionName(value)) {
        setData((prevData) => ({
          ...prevData,
          [name]: value
        }));
      }
      return;
    }

    setData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  }

  useEffect(() => {
    if (keypairResponse?.publicKey) {
      setData((prevData) => ({
        ...prevData,
        permission_public_key: keypairResponse?.publicKey
      }));
    }
  }, [keypairResponse]);

  useEffect(() => {
    if (!data.permission_name || !data.permission_parent_private_key || !data.permission_public_key) {
      setIsValid(false);
    } else {
      setIsValid(true);
    }
  }, [data]);

  useEffect(() => {
    if (response?.status && !error) {
      setTimeout(() => {
        resetServerStatus();
        handleModal(false);
        window.location.reload();
      }, finalStepDuration || 1600);
    }
  }, [response, error]);

  const handleCreate = (event) => {
    event.preventDefault();
    setNewPermission();
  }

  const handleExportKeys = () => {
    if (keypairResponse?.publicKey && keypairResponse?.privateKey) {
      const content = `Public Key:\n${keypairResponse.publicKey}\n\nPrivate Key:\n${keypairResponse.privateKey}`;
      const filename = `permission_keys_${Date.now()}.txt`;
      downloadFile(content, filename, { type: 'text/plain;charset=utf-8;' });
    }
  }

  if (loading) {
    return (
      <ModalShell>
        <Loader />
      </ModalShell>
    );
  }

  if (response?.status) {
    return (
      <ModalShell>
        <SuccessfullActionModal message={response?.message} />
      </ModalShell>
    );
  }

  return (
    <ModalShell closeOnOverlay={true}>
      <div className={`${styles.component} ${dark ? styles.dark : ""}`}>
        <div className={styles.label}>Create new permission</div>
        <form id='create_permission_form' onSubmit={handleCreate}>
          <div className={styles.input_group}>
            <p>Set new permission name</p>
            <div className={styles.input_block}>
              <input
                type="text"
                placeholder="Permission name"
                name='permission_name'
                value={data.permission_name}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className={styles.input_group}>
            <p>Set new permission public key</p>
            <div className={`${styles.input_block} ${styles.input_block__with_btn}`}>
              <input
                type="text"
                placeholder="Public key"
                name='permission_public_key'
                value={data.permission_public_key}
                onChange={handleInputChange}
              />
              <button type='button' onClick={() => getKeypair()}>Generate new keypair</button>
            </div>
          </div>

          {keypairResponse?.privateKey && <div className={styles.input_group}>
            <p>New permission private key</p>
            <div className={styles.key_shell}>
              <span>{keypairResponse?.privateKey}</span>
              <button 
                type="button" 
                className={styles.export_button}
                onClick={handleExportKeys}
                title="Export keys to text file"
              >
                <ExportIcon />
              </button>
            </div>
          </div>}

          <div className={styles.input_group}>
            <p>"{permissionParentName}" permission private key</p>
            <div className={styles.input_block}>
              <input
                type="text"
                placeholder="Private key"
                name='permission_parent_private_key'
                value={data.permission_parent_private_key}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </form>
        {error && <div className={styles.form_msg}><span>{error}</span></div>}
        <div className={styles.form_actions}>
          <button
            type='submit'
            form='create_permission_form'
            disabled={!isValid}
          >
            Create
          </button>
          <button onClick={() => handleModal(false)}>Cancel</button>
        </div>
      </div>
    </ModalShell>
  );
}

export default CreatePermissionModal;